import type { FC } from "react";
import { useEffect, useState } from "react";
import type { RegisterOptions } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";

import { InputEditButton, TextInput } from "@ui/components/Inputs";

export type FormTextInputProps = {
  id: string;
  label: string;
  placeholder?: string;
  disclaimer?: string;
  validationRules: RegisterOptions;
  matchingFieldId?: string;
  mathchingFieldErrorMessage?: string;
  dataTestId?: string;
  isLocked?: boolean;
  mode?: "standard" | "editable";
};

export const FormTextInput: FC<FormTextInputProps> = ({
  id,
  label,
  placeholder,
  disclaimer,
  validationRules,
  matchingFieldId,
  mathchingFieldErrorMessage,
  dataTestId = "formTextInput",
  mode,
  isLocked,
}) => {
  const { watch, trigger, formState } = useFormContext();
  const matchingFieldValue = matchingFieldId ? watch(matchingFieldId) : undefined;
  const value = watch(id);
  const [isEditable, setIsEditable] = useState<boolean>(mode === "editable");

  useEffect(() => {
    setIsEditable(mode === "editable");
  }, [mode]);

  useEffect(() => {
    if (value) {
      trigger(id);
    }
  }, [id, value, trigger]);

  useEffect(() => {
    if (matchingFieldId && formState.errors[id]) {
      trigger(id);
    }
  }, [id, matchingFieldId, matchingFieldValue, trigger]);

  const validationRulesWithMatching = {
    ...validationRules,
    validate: (value: string) => {
      if (!matchingFieldId || !matchingFieldValue) {
        return true;
      }
      return value === matchingFieldValue || mathchingFieldErrorMessage;
    },
  };

  return (
    <Controller
      name={id}
      defaultValue=""
      rules={validationRulesWithMatching}
      render={({ field, fieldState: { error } }) => (
        <TextInput
          {...field}
          id={id}
          placeholder={placeholder}
          data-testid={dataTestId}
          required={!!validationRules.required}
          necessityIndicator
          label={label}
          disabled={isLocked}
          readOnly={isEditable}
          elemAfter={isEditable ? <InputEditButton onClick={() => setIsEditable(false)} /> : undefined}
          isError={!!error}
          errorText={error?.message}
          helperText={disclaimer}
        />
      )}
    />
  );
};
